import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { apiFetch } from '../api.js'

const TYPE_COLORS = {
  error: 'text-red-600 bg-red-50',
  warning: 'text-amber-600 bg-amber-50',
  info: 'text-blue-600 bg-blue-50',
  security: 'text-red-700 bg-red-50',
  settings: 'text-orange-600 bg-orange-50',
}

const CATEGORY_ICONS = {
  email: '✉',
  campaign: '📢',
  stock: '📦',
  payment: '💳',
  security: null,
  settings: null,
}

function SecurityIcon({ severity }) {
  const cls = severity === 'HIGH'
    ? 'text-red-600'
    : severity === 'MEDIUM'
      ? 'text-amber-500'
      : 'text-slate-500'
  return <Shield size={16} className={cls} />
}

function SettingsIcon() {
  return <Shield size={16} className="text-orange-500" />
}

function alertBadgeClass(a) {
  if (a.type === 'security' || a.category === 'security') {
    return a.severity === 'HIGH' ? 'text-red-700 bg-red-50' : 'text-amber-600 bg-amber-50'
  }
  if (a.type === 'settings' || a.category === 'settings') {
    return 'text-orange-600 bg-orange-50'
  }
  return TYPE_COLORS[a.type] || 'text-slate-600 bg-slate-50'
}

function alertBadgeLabel(a) {
  if (a.type === 'security' || a.category === 'security') {
    return a.severity === 'HIGH' ? 'CRITIQUE' : 'ALERTE'
  }
  if (a.type === 'settings' || a.category === 'settings') {
    return 'WARNING'
  }
  return a.type || 'info'
}

function AlertIcon({ alert }) {
  if (alert.type === 'security' || alert.category === 'security') {
    return <SecurityIcon severity={alert.severity} />
  }
  if (alert.type === 'settings' || alert.category === 'settings') {
    return <SettingsIcon />
  }
  return <span className="text-base mt-0.5">{CATEGORY_ICONS[alert.category] || '🔔'}</span>
}

export default function NotificationBell() {
  const [alerts, setAlerts] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const buttonRef = useRef(null)
  const panelRef = useRef(null)
  const navigate = useNavigate()

  function load() {
    setLoading(true)
    apiFetch('/api/settings/notifications')
      .then((d) => setAlerts(d.alerts || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  // Poll every 2 minutes
  useEffect(() => {
    load()
    const interval = setInterval(load, 120_000)
    return () => clearInterval(interval)
  }, [])

  // Position + close on outside click
  useEffect(() => {
    if (!open) return

    const updatePos = () => {
      if (!buttonRef.current) return
      const rect = buttonRef.current.getBoundingClientRect()
      const panelWidth = 320
      const margin = 8
      // Open to the right of the sidebar
      let left = rect.right + margin
      // If it would go off screen on the right, flip left
      if (left + panelWidth > window.innerWidth - margin) {
        left = rect.left - panelWidth - margin
      }
      setDropdownPos({ top: rect.bottom + 4, left })
    }

    const handleClick = (e) => {
      if (buttonRef.current?.contains(e.target)) return
      if (panelRef.current?.contains(e.target)) return
      setOpen(false)
    }

    updatePos()
    document.addEventListener('mousedown', handleClick)
    window.addEventListener('resize', updatePos)
    window.addEventListener('scroll', updatePos, true)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      window.removeEventListener('resize', updatePos)
      window.removeEventListener('scroll', updatePos, true)
    }
  }, [open])

  function handleAlertClick(alert) {
    setOpen(false)
    if (alert.action) navigate(alert.action)
  }

  const count = alerts.length

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
        title="Notifications"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[100] w-80 bg-white rounded-xl border border-slate-200 shadow-lg"
          style={{ top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px` }}
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Notifications</span>
            {loading && <span className="text-xs text-slate-400">Actualisation...</span>}
          </div>

          {alerts.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              Aucune alerte active.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto divide-y divide-slate-50">
              {alerts.map((a, i) => (
                <button
                  key={i}
                  onClick={() => handleAlertClick(a)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 shrink-0">
                      <AlertIcon alert={a} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">{a.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5 leading-snug">{a.message}</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${alertBadgeClass(a)}`}>
                      {alertBadgeLabel(a)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="px-4 py-2 border-t border-slate-100 text-right">
            <button
              onClick={() => { setOpen(false); navigate('/settings?tab=usage'); }}
              className="text-xs text-slate-500 hover:text-slate-700 hover:underline"
            >
              Voir les stats →
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
